import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
// @mui
import {
  CardContent,
  Container,
  Typography,
  Grid,
  Card,
  CardHeader,
  Stepper,
  Step,
  Box,
  Button,
  StepLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import api from 'src/utils/api';
// components
import Iconify from '../../components/iconify';

// ----------------------------------------------------------------------

const getYouTubeID = (url) => {
  if (!url) return '';
  if (!url.includes('/') && url.length === 11) return url;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : url;
};

const MIN_TASK_TIME_MS = 60000;

// Task iframe with locked overlay and play button (copied from Homeworks)
const TaskIframe = ({ htmlContent, onInteract }) => {
  const [loading, setLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(true);

  useEffect(() => {
    setLoading(true);
    setIsLocked(true);
  }, [htmlContent]);

  if (!htmlContent) return null;

  const srcMatch = htmlContent.match(/src="([^"]+)"/);
  const src = srcMatch ? srcMatch[1] : null;

  if (!src) {
    // eslint-disable-next-line react/no-danger
    return <div className="iframe" dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  }

  const handleStartClick = () => {
    setIsLocked(false);
    if (onInteract) {
      onInteract();
    }
  };

  return (
    <Box 
      sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '360px', 
        bgcolor: '#fff', 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
        overflow: 'hidden',
        maskImage: 'radial-gradient(white, black)',
        WebkitMaskImage: '-webkit-radial-gradient(white, black)',
        transform: 'translate3d(0,0,0)', 
      }}
    >
      <style>
        {`@keyframes crawl { to { stroke-dashoffset: -100; } } .worm-rect { animation: crawl 20s linear infinite; }`}
      </style>
      
      {loading && (
        <Box
          sx={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
            bgcolor: 'white'
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {!loading && isLocked && (
        <Box
          onClick={handleStartClick}
          sx={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
            zIndex: 10,
            bgcolor: '#FFF59D',
            cursor: 'pointer',
            transition: 'all 0.4s ease',
            '&:hover': {
              bgcolor: '#FFF176',
              '& .play-button': { transform: 'scale(1.1)' }
            },
            p: 3, 
          }}
        >
          <Box sx={{ position: 'absolute', top: 24, left: 24, right: 24, bottom: 24, pointerEvents: 'none' }}>
            <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
              <rect
                className="worm-rect"
                x="6" y="6" 
                width="calc(100% - 12px)" 
                height="calc(100% - 12px)"
                rx="16" ry="16"
                fill="none"
                stroke="#FF6D00" 
                strokeWidth="12" 
                strokeLinecap="round"
                pathLength="100"
                strokeDasharray="12.5 87.5" 
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </Box>

          <Box 
            className="play-button"
            sx={{ 
              width: 90, 
              height: 90, 
              bgcolor: '#FF6D00', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(255, 109, 0, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              position: 'relative', 
              zIndex: 2 
            }}
          >
             <svg width="34" height="34" viewBox="0 0 24 24" fill="white" 
               style={{ display: 'block', transform: 'translateX(3px)' }}
             >
                <path d="M18.2 10.6L7.8 4.2C6.5 3.4 4.9 4.4 4.9 5.9V18.1C4.9 19.6 6.5 20.6 7.8 19.8L18.2 13.4C19.5 12.6 19.5 10.6 18.2 10.6Z" />
             </svg>
          </Box>
        </Box>
      )}

      <iframe
        src={src}
        onLoad={() => setLoading(false)}
        title="Interactive Task"
        style={{
          width: '100%', height: '100%', border: 'none',
          display: 'block',
          opacity: loading ? 0 : 1, transition: 'opacity 0.5s',
        }}
        allowFullScreen
      />
    </Box>
  );
};

TaskIframe.propTypes = {
  htmlContent: PropTypes.string,
  onInteract: PropTypes.func,
};

export default function SelfstudyPage() {
  const [check, setCheck] = useState(0);
  const [alert, setAlert] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [videos, setVideos] = useState([]);
  
  // Interaction state
  const [isTaskInteracted, setIsTaskInteracted] = useState(false);
  const [interactionStartTime, setInteractionStartTime] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const saveProgress = useCallback(() => {
    const storedJson = JSON.stringify({ check, activeStep });
    localStorage.setItem('SelfStudy', storedJson);
  }, [check, activeStep]);

  const readProgress = () => {
    const storedJson = JSON.parse(localStorage.getItem('SelfStudy'));
    if (storedJson && storedJson.check !== undefined) {
      setCheck(storedJson.check);
      setActiveStep(storedJson.activeStep);
    }
  };

  useEffect(() => {
    readProgress();
  }, []);

  useEffect(() => {
    if (activeStep) saveProgress();
  }, [activeStep, saveProgress]);

  useEffect(() => {
    if (check) saveProgress();
  }, [check, saveProgress]);

  // Reset interaction state when step or lesson changes
  useEffect(() => {
    setIsTaskInteracted(false);
    setInteractionStartTime(null);
  }, [activeStep, check]);

  useEffect(() => {
    api.get('video-api?filter[type]=4')
      .then((result) => {
        readProgress();
        setVideos(result);
      })
      .catch(() => {
        setVideos([]);
      });
  }, []);

  const handleTaskStart = () => {
    if (!isTaskInteracted) {
      setIsTaskInteracted(true);
      setInteractionStartTime(Date.now());
    }
  };

  const handleNextStep = () => {
    const currentVideo = videos[check];
    let hasTask = false;
    
    // Check if current step has a task
    if (activeStep === 0 && currentVideo.student_material) hasTask = true;
    else if (activeStep === 1 && currentVideo.student_material2) hasTask = true;
    else if (activeStep === 2 && currentVideo.student_material3) hasTask = true;

    if (hasTask) {
      if (!isTaskInteracted) {
        setSnackbar({ open: true, message: 'Натисніть кнопку Play на завданні, щоб розпочати!' });
        return;
      }

      if (interactionStartTime) {
        const timeSpent = Date.now() - interactionStartTime;
        if (timeSpent < MIN_TASK_TIME_MS) {
          setSnackbar({ open: true, message: 'Попрактикуйся ще, щоб закріпити матеріал' });
          return;
        }
      }
    }

    setActiveStep(activeStep + 1);
  };

  return (
    <>
      <Helmet>
        <title> Правила | Lala English </title>
      </Helmet>

      <Alert severity="info" sx={{ mb: 5, pl: 3 }}>
        Тут ти можеш виконувати уроки самостійно
      </Alert>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
           <FormControl sx={{ width: '300px' }}>
            <InputLabel id="demo-simple-select-label">Урок</InputLabel>
            <Select
              value={videos.length > 0 ? check : ''}
              label="Урок"
              onChange={(event) => setCheck(event.target.value)}
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiSelect-icon': { display: 'none' } }}
            >
              {videos.map((value, index) => (
                  <MenuItem key={value.id} value={index}>
                    {value.position}. {value.title}
                  </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Box component="div" sx={{ height: 40, display: 'inline-flex' }}>
            <img src={'/assets/images/prizes/contacts__bottom-animation.svg'} alt={'lala english svg'} />
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" alignItems="center" spacing={3}>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:arrow-left-fill" />}
              disabled={check === 0}
              onClick={() => {
                setCheck(check - 1);
                setActiveStep(2); // Goes to last step of previous lesson
                saveProgress();
              }}
            >
              Попередній урок
            </Button>
            <Button
              variant="outlined"
              endIcon={<Iconify icon="eva:arrow-right-fill" />}
              disabled={check === videos.length - 1}
              onClick={() => {
                if (activeStep < 2) setAlert(true);
                else {
                  setCheck(check + 1);
                  setActiveStep(0);
                  saveProgress();
                }
              }}
            >
              Наступний урок
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={3}>
          {!videos.length ? (
            <Typography variant="body2" sx={{ p: 3.5 }}>
              Ще немає відео
            </Typography>
          ) : (
            <Grid key={videos[check].id} container spacing={5}>
           <Grid item xs={12} md={6}>
             {/* Left: tasks with TaskIframe */}
             <Card sx={{ height: 'auto', display: 'flex', flexDirection: 'column', mb: 3, ml: { xs: 0, md: 3 }, mt: { xs: 0, md: 3 } }}>
                    <CardHeader title={`Завдання № ${activeStep + 1}`} />
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      {/* Added key={activeStep} to force re-render of component when step changes */}
                      {videos[check].student_material && activeStep === 0 ? (
                        <TaskIframe 
                          key="step0" 
                          htmlContent={videos[check].student_material} 
                          onInteract={handleTaskStart} 
                        />
                      ) : videos[check].student_material2 && activeStep === 1 ? (
                        <TaskIframe 
                          key="step1" 
                          htmlContent={videos[check].student_material2} 
                          onInteract={handleTaskStart} 
                        />
                      ) : videos[check].student_material3 && activeStep === 2 ? (
                        <TaskIframe 
                          key="step2" 
                          htmlContent={videos[check].student_material3} 
                          onInteract={handleTaskStart} 
                        />
                      ) : (
                        <Typography component="span" sx={{ color: 'text.secondary', fontSize: 14, textAlign: 'center', width: '100%', py: 5 }}>
                          В цьому уроці немає завдань
                        </Typography>
                      )}
                    </Box>

                    <div className="hideword" />
                    <Stepper activeStep={activeStep} sx={{ marginTop: '10px', padding: '10px' }}>
                      {[...Array(3)].map((_, index) => (
                        <Step key={index}><StepLabel /></Step>
                      ))}
                    </Stepper>
                    <Box sx={{ display: 'flex', flexDirection: 'row', padding: '10px' }}>
                      <Button 
                        color="inherit" 
                        disabled={activeStep === 0} 
                        onClick={() => setActiveStep(activeStep - 1)} 
                        sx={{ mr: 1 }}
                      >
                        Назад
                      </Button>
                      <Box sx={{ flex: '1 1 auto' }} />
                      <Button 
                        disabled={activeStep === 2} 
                        onClick={handleNextStep} 
                        sx={{ mr: 1 }}
                      >
                        Наступне завдання
                      </Button>
                    </Box>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6} lg={6}>
                  {/* Right: video */}
                  <Card sx={{ position: 'relative', ml: 0, mt: 3 }}>
                    <CardHeader title="Пояснення" sx={{ mb: 2 }}/>
                    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                      <iframe
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                        src={`https://www.youtube.com/embed/${getYouTubeID(videos[check].url)}`}
                        title={videos[check].title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>

                    <CardContent sx={{ pt: 2 }}>
                      {videos[check].text ? (
                        <Typography variant="body2" sx={{ pt: 0 }}>
                          {videos[check].text}
                        </Typography>
                      ) : null}
                    </CardContent>
                  </Card>
                </Grid>
            </Grid>
          )}
          <Snackbar open={alert} autoHideDuration={4000} onClose={() => setAlert(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert severity="info" variant="filled" onClose={() => setAlert(false)} sx={{ width: '100%' }}>
              Спочатку пройдіть усі завдання в поточному уроці
            </Alert>
          </Snackbar>

          <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert severity="info" variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Grid>
      </Container>
    </>
  );
}